import { useState, useMemo, useEffect, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { useDrop } from 'react-dnd';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { AnimatedIconButton } from '../../common/AnimatedIconButton';
// V4-CODESPLIT-DASHBOARD 2026-07-29 — sortie du chunk d'entrée : la modale ne
// s'ouvre que sur sélection/création d'un post, jamais au premier rendu.
const ExpandedPostModal = lazy(() =>
  import('./ExpandedPostModal').then(m => ({ default: m.ExpandedPostModal }))
);
import { PostDeleteConfirmModal } from './PostDeleteConfirmModal';
import { ClearGalleryConfirmModal } from '../../common/ClearGalleryConfirmModal';
import { MobileFiltersModal } from '../generation/MobileFiltersModal';
import { useIsMobile } from '../../../hooks/useIsMobile';
import type { Post, CommunicationChannel, WatchTopic, Source } from '../../../types';
import { SortType } from '../../common/SortSelector';
import { PostStatusFilter } from '../../../utils/Constants';
import { usePostFilters, type MobileHeaderControls } from './gallery/usePostFilters';
import { useGridLayout } from './gallery/useGridLayout';
import { PostGalleryHeader } from './gallery/PostGalleryHeader';
import { PostGalleryGrid } from './gallery/PostGalleryGrid';
import { useSetupWizard } from '../../../hooks/useSetupWizard';
import { useLanguage } from '../../../contexts/LanguageContext';
import { toastService } from '../../../services/toastService';
import { usePostsContext } from '../../../contexts/PostsContext';

interface PostGalleryProps {
  onOpenGenerateModal: () => void;
  communicationChannels: CommunicationChannel[];
  onCardLayoutChange?: (width: number, gap: number) => void;
  onOpenBurger?: () => void;
  logoUrl?: string;
  isBurgerOpen?: boolean;
  // V4-FRONT-ANY-USAGE batch 6 2026-05-29 — type partagé (usePostFilters / DashboardPage)
  onHeaderControlsReady?: (controls: MobileHeaderControls) => void;
  onOpenSidebarTab?: (tabId: string) => void;
  systemPrompt?: string;
  isPositioningTemplate?: boolean;
  // V4-ONBOARDING-CHECKLIST-OPTIONAL — topics/sources optionnels si veille off.
  sectorWatchEnabled?: boolean;
  watchTopics?: WatchTopic[];
  sources?: Source[];
  onRefreshData?: () => Promise<void>;
  isDataLoading?: boolean;
  onPublishNow?: (postId: string) => Promise<Post | null>;
  onPostModalOpenChange?: (open: boolean) => void;
}

export function PostGallery({
  onOpenGenerateModal,
  communicationChannels,
  onCardLayoutChange,
  onHeaderControlsReady,
  onOpenSidebarTab,
  systemPrompt,
  isPositioningTemplate,
  sectorWatchEnabled,
  watchTopics,
  sources,
  onRefreshData,
  isDataLoading,
  onPublishNow,
  onPostModalOpenChange,
}: PostGalleryProps) {
  const {
    posts,
    queue: queuePosts,
    createPost: onCreatePost,
    deletePost: onDeletePost,
    updatePost: onUpdatePost,
    unschedulePost: onUnschedulePost,
    duplicatePost: onDuplicatePost,
    addToQueue: onAddToQueue,
    addToNewsletter: onAddToNewsletter,
  } = usePostsContext();
  const { t } = useLanguage();
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [deletingPost, setDeletingPost] = useState<Post | null>(null);
  const isMobile = useIsMobile();

  const { isComplete: isSetupComplete } = useSetupWizard({
    systemPrompt: systemPrompt ?? '',
    isPositioningTemplate,
    watchTopics: watchTopics ?? [],
    sources: sources ?? [],
    communicationChannels,
    sectorWatchEnabled,
  });

  const {
    searchQuery, setSearchQuery,
    startDate, setStartDate,
    endDate, setEndDate,
    sortType, setSortType,
    sortOrder, setSortOrder,
    statusFilter, setStatusFilter,
    showExternal, handleToggleExternal,
    showInternal, handleToggleInternal,
    showOnlyMine, handleToggleOnlyMine, canFilterMine,
    availableChannels,
    selectedChannelIds, toggleChannel,
    channelOffset, scrollChannels,
    visibleChannelsCount,
    showFilters, setShowFilters,
    isCreatingNewPost, setIsCreatingNewPost,
    showClearGalleryConfirm, setShowClearGalleryConfirm,
    filteredAvailableChannels,
    visibleChannels,
    filteredPosts
  } = usePostFilters({
    posts,
    communicationChannels,
    isMobile,
    onHeaderControlsReady,
    onOpenGenerateModal,
    onCreatePost
  });

  const {
    gridGap,
    numColumns,
    cardWidth,
    gridContainerRef
  } = useGridLayout({ onCardLayoutChange }) as {
    gridGap: number;
    numColumns: number;
    cardWidth: number;
    gridContainerRef: React.RefObject<HTMLDivElement>;
  };

  // Notify parent when any post modal opens/closes (to hide MobileBottomBar)
  useEffect(() => {
    onPostModalOpenChange?.(selectedPost !== null || isCreatingNewPost);
  }, [selectedPost, isCreatingNewPost, onPostModalOpenChange]);

  // Pagination côté frontend pour éviter de rendre 1000+ posts simultanément
  const PAGE_SIZE = 50;
  const [galleryPage, setGalleryPage] = useState(0);
  const paginatedPosts = filteredPosts.slice(galleryPage * PAGE_SIZE, (galleryPage + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filteredPosts.length / PAGE_SIZE);

  // Generate a new UUID for creating posts - always valid
  const newPostTemplate = useMemo((): Post | null => {
    if (!isCreatingNewPost || communicationChannels.length === 0) return null;
    
    return {
      id: crypto.randomUUID(),
      channelId: communicationChannels[0].id,
      content: '',
      generatedDate: new Date(),
    };
  }, [isCreatingNewPost, communicationChannels]);

  // Drop zone to accept posts from queue
  const [, drop] = useDrop(() => ({
    accept: 'post',
    drop: (item: Post) => {
      // Check if the post has a scheduledDate (meaning it's from the queue)
      if (item.scheduledDate) {
        // Logic to move back to gallery handled by parent or state update
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  return (
    <div 
      ref={drop as unknown as React.RefObject<HTMLDivElement>}
      className="h-full flex flex-col relative"
      style={{ backgroundColor: isMobile ? 'transparent' : 'var(--color-surface)' }}
    >

      <PostGalleryHeader
        isMobile={isMobile}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        sortType={sortType}
        sortOrder={sortOrder}
        setSortType={setSortType}
        setSortOrder={setSortOrder}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        setIsCreatingNewPost={setIsCreatingNewPost}
        onOpenGenerateModal={onOpenGenerateModal}
        setShowClearGalleryConfirm={setShowClearGalleryConfirm}
        showExternal={showExternal}
        handleToggleExternal={handleToggleExternal}
        showInternal={showInternal}
        handleToggleInternal={handleToggleInternal}
        showOnlyMine={showOnlyMine}
        handleToggleOnlyMine={handleToggleOnlyMine}
        canFilterMine={canFilterMine}
        scrollChannels={scrollChannels}
        channelOffset={channelOffset}
        filteredAvailableChannels={filteredAvailableChannels}
        visibleChannelsCount={visibleChannelsCount}
        visibleChannels={visibleChannels}
        selectedChannelIds={selectedChannelIds}
        toggleChannel={toggleChannel}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        isSetupComplete={isSetupComplete}
      />

      {/* Mobile: pagination en haut dans le flux */}
      {isMobile && totalPages > 1 && (
        <div
          className="flex items-center justify-center gap-4 px-4 py-2 flex-shrink-0"
          style={{ borderBottom: '1px solid var(--color-border)' }}
        >
          <AnimatedIconButton
            icon={ChevronLeft}
            label={t.posts.previousPage || 'Précédent'}
            disabled={galleryPage === 0}
            onClick={() => setGalleryPage(p => p - 1)}
            variant="secondary"
            revealLabelOnHover={true}
          />
          <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            {galleryPage + 1} / {totalPages} · {filteredPosts.length} posts
          </span>
          <AnimatedIconButton
            icon={ChevronRight}
            label={t.posts.nextPage || 'Suivant'}
            disabled={galleryPage >= totalPages - 1}
            onClick={() => setGalleryPage(p => p + 1)}
            variant="secondary"
            revealLabelOnHover={true}
          />
        </div>
      )}

      <PostGalleryGrid
        isMobile={isMobile}
        filteredPosts={paginatedPosts}
        numColumns={numColumns}
        cardWidth={cardWidth}
        gridGap={gridGap}
        gridContainerRef={gridContainerRef}
        onOpenGenerateModal={onOpenGenerateModal}
        setSelectedPost={setSelectedPost}
        setDeletingPost={setDeletingPost}
        communicationChannels={communicationChannels}
        onOpenSidebarTab={onOpenSidebarTab}
        systemPrompt={systemPrompt}
        isPositioningTemplate={isPositioningTemplate}
        sectorWatchEnabled={sectorWatchEnabled}
        watchTopics={watchTopics}
        sources={sources}
        onRefreshData={onRefreshData}
        isDataLoading={isDataLoading}
        // V4-FRONT-WIZARD-POST-GEN-HIDE-VERIFY 2026-05-29 — passe le raw count
        // (posts + queue) pour que shouldShowWizard ne se ré-active pas quand
        // l'user filtre un status vide.
        hasAnyPost={posts.length + queuePosts.length > 0}
      />

      {/* Desktop: pagination en bas au centre via portail (échappe aux overflow-hidden parents) */}
      {!isMobile && totalPages > 1 && createPortal(
        <div
          style={{
            position: 'fixed',
            bottom: '16px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 35,
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            padding: '8px 24px',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            backgroundColor: 'rgba(var(--color-background-rgb), 0.8)',
            border: '1px solid var(--color-border)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05)',
            borderRadius: '30px',
          }}
        >
          <AnimatedIconButton
            icon={ChevronLeft}
            label={t.posts.previousPage || 'Précédent'}
            disabled={galleryPage === 0}
            onClick={() => setGalleryPage(p => p - 1)}
            variant="secondary"
            revealLabelOnHover={true}
          />
          <span style={{ fontSize: '14px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
            {galleryPage + 1} / {totalPages} · {filteredPosts.length} posts
          </span>
          <AnimatedIconButton
            icon={ChevronRight}
            label={t.posts.nextPage || 'Suivant'}
            disabled={galleryPage >= totalPages - 1}
            onClick={() => setGalleryPage(p => p + 1)}
            variant="secondary"
            revealLabelOnHover={true}
          />
        </div>,
        document.body
      )}

      {isMobile && (
        <MobileFiltersModal
          isOpen={showFilters}
          onClose={() => setShowFilters(false)}
          availableChannels={filteredAvailableChannels}
          selectedChannelIds={selectedChannelIds}
          onToggleChannel={toggleChannel}
          showExternal={showExternal}
          showInternal={showInternal}
          onToggleExternal={handleToggleExternal}
          onToggleInternal={handleToggleInternal}
          showOnlyMine={showOnlyMine}
          onToggleOnlyMine={handleToggleOnlyMine}
          canFilterMine={canFilterMine}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
        />
      )}

      {selectedPost && (
        <Suspense fallback={null}>
        <ExpandedPostModal
          post={selectedPost}
          onClose={() => setSelectedPost(null)}
          onAddToQueue={onAddToQueue}
          onAddToNewsletter={onAddToNewsletter}
          onDelete={onDeletePost}
          onDuplicatePost={onDuplicatePost}
          onUpdatePost={onUpdatePost}
          onUnschedule={onUnschedulePost}
          onPublishNow={onPublishNow}
          communicationChannels={communicationChannels}
        />
        </Suspense>
      )}

      {deletingPost && (
        <PostDeleteConfirmModal
          post={deletingPost}
          onClose={() => setDeletingPost(null)}
          onDelete={onDeletePost}
        />
      )}

      {showClearGalleryConfirm && (
        <ClearGalleryConfirmModal
          onClose={() => setShowClearGalleryConfirm(false)}
          onConfirm={() => {
            posts.forEach(post => onDeletePost(post.id));
            setShowClearGalleryConfirm(false);
            toastService.success(t.toasts.galleryCleared, t.toasts.galleryClearedDesc);
          }}
        />
      )}

      {isCreatingNewPost && newPostTemplate && (
        <Suspense fallback={null}>
        <ExpandedPostModal
          post={newPostTemplate}
          onClose={() => {
            setIsCreatingNewPost(false);
          }}
          onCreatePost={onCreatePost}
          communicationChannels={communicationChannels}
          isCreating={true}
        />
        </Suspense>
      )}
    </div>
  );
}
